import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/permissions/me');
      setPermissions(data);
    } catch {
      setPermissions(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      await fetchPermissions();
    } catch {
      setUser(false);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data);
    await fetchPermissions();
    return data;
  };

  const updateMyPreferences = async (prefs) => {
    const { data } = await api.put('/users/me/preferences', prefs);
    // Optimistically merge into local user state so the sidebar repaints immediately.
    setUser(prev => prev ? { ...prev, ...prefs } : prev);
    return data;
  };

  const register = async (email, password, name, role) => {
    const { data } = await api.post('/auth/register', { email, password, name, role });
    setUser(data);
    await fetchPermissions();
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
    setPermissions(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, permissions, refreshPermissions: fetchPermissions, refreshAuth: checkAuth, updateMyPreferences }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
